import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Download, ArrowLeft } from "lucide-react";
import ResultsHeader from "@/components/results/ResultsHeader";
import EvidenceSection from "@/components/results/EvidenceSection";
import EvidenceOverview from "@/components/results/EvidenceOverview";
import { useResultsData } from "@/hooks/useResultsData";

const LiteratureReview = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { answers, result, aiScore, confidence } = location.state || {};
  
  const { enhancedData, getTotalEvidence } = useResultsData(answers);

  const handleDownloadReport = () => {
    // Generate and download comprehensive report
    const reportData = {
      score: aiScore,
      assessment: result?.category || 'General Assessment',
      symptoms: answers,
      recommendations: result?.nextSteps || [],
      evidence: enhancedData
    };
    
    // Create downloadable content
    const reportContent = `
AmIOK Health Assessment Report
============================

Assessment Type: ${reportData.assessment}
AmIOK Score: ${reportData.score}/100
Confidence Level: ${confidence}%

Symptoms Reported:
${JSON.stringify(answers, null, 2)}

Evidence Summary:
Total Sources: ${getTotalEvidence()}
Research Quality: ${enhancedData?.evidenceLevel || 'Medium'}

Recommendations:
${reportData.recommendations.join('\n- ')}

Generated on: ${new Date().toLocaleDateString()}
    `;

    const blob = new Blob([reportContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'amiok-health-report.txt';
    a.click();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <ResultsHeader />
      
      <main className="container mx-auto px-6 py-8">
        <div className="max-w-6xl mx-auto space-y-8">
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              onClick={() => navigate(-1)}
              className="flex items-center"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Results
            </Button>
            
            <Button
              onClick={handleDownloadReport}
              className="bg-green-600 hover:bg-green-700 flex items-center"
            >
              <Download className="mr-2 h-4 w-4" />
              Download Full Report
            </Button>
          </div>

          <Card className="p-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-6">Literature Review & Evidence Analysis</h1>
            
            {enhancedData && (
              <EvidenceOverview
                evidenceLevel={enhancedData.evidenceLevel}
                confidence={confidence}
                totalEvidence={getTotalEvidence()}
                relevantResearchCount={enhancedData.relevantResearch.length}
              />
            )}
          </Card>

          {enhancedData && (
            <EvidenceSection
              relevantResearch={enhancedData.relevantResearch}
              clinicalGuidelines={enhancedData.clinicalGuidelines}
              drugSafetyInfo={enhancedData.drugSafetyInfo}
            />
          )}
        </div>
      </main>
    </div>
  );
};

export default LiteratureReview;